// MaintenanceOverlay.tsx · v12.10.30 (#2255 practice offline) · 2026-11-03 · VoiceSpectrum AI
// MaintenanceOverlay.tsx - v12.10.18 (#1859: "Looks like you're offline" when it is the user's own connection; English only, Tushar 10/20)
// v11.8.7.2
// Shared branded maintenance overlay. Mounted on BOTH the authed app shell (Shell.tsx) and the
// login page (LoginPage.tsx) so a confirmed API outage shows the same full-screen "be right back"
// experience everywhere — including before sign-in. Uses useApiHealth() internally; renders nothing
// until an outage is confirmed. Styles are self-contained (MaintenanceOverlay.css, unscoped) so the
// overlay looks identical inside and outside the .tenant-app shell.
import { useEffect, useState } from 'react';
import { useApiHealth } from '../lib/useApiHealth';
import ReportIssue from './ReportIssue';   // v12.10.30 (#2255)
import './MaintenanceOverlay.css';

function MaintStatus({ since }: { since: number | null }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  const elapsedSec = since ? Math.max(0, Math.floor((now - since) / 1000)) : 0;
  const mins = Math.floor(elapsedSec / 60);
  const secs = elapsedSec % 60;
  const elapsed = mins > 0 ? `${mins}m ${secs.toString().padStart(2, '0')}s` : `${secs}s`;
  const sinceTime = since ? new Date(since).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
  const stamp = new Date(now).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' });
  return (
    <div className="vs-maint-status">
      {since !== null && <div className="vs-maint-since">Offline since {sinceTime} &middot; {elapsed}</div>}
      <div className="vs-maint-stamp">As of {stamp}</div>
    </div>
  );
}

export default function MaintenanceOverlay() {
  const { down, since, offline } = useApiHealth();
  if (!down) return null;
  if (offline) return (
    <div className="vs-maint-overlay" role="alert" aria-live="assertive">
      <div className="vs-maint-card">
        {/* offline: the logo may not be in the cache - hide a broken image rather than show one */}
        <img className="vs-maint-lockup" src="/brand/svg/19_compact_lockup_light.svg" alt="VoiceSpectrum AI" onError={e => { e.currentTarget.style.display = 'none'; }} />
        <div className="vs-maint-title">Looks like you&rsquo;re offline</div>
        <p className="vs-maint-msg">We can&rsquo;t reach the internet from this device. Check your Wi-Fi or internet connection, and VoiceSpectrum will pick up right where you left off.</p>
        <div className="vs-maint-dots" aria-hidden="true"><i /><i /><i /></div>
        <div className="vs-maint-reconnect">Waiting for your internet connection to be restored&hellip;</div>
        <MaintStatus since={since} />
        {/* v12.10.30 (#2255): Report issue - kept on this device, sent when the connection is back (one ticket per practice) */}
        <ReportIssue kind="offline" downSince={since} />
      </div>
    </div>
  );
  return (
    <div className="vs-maint-overlay" role="alert" aria-live="assertive">
      <div className="vs-maint-card">
        <img className="vs-maint-lockup" src="/brand/svg/19_compact_lockup_light.svg" alt="VoiceSpectrum AI" />
        <div className="vs-maint-title">We&rsquo;ll be right back</div>
        <p className="vs-maint-msg">VoiceSpectrum is briefly offline for maintenance. You&rsquo;ll be reconnected automatically as soon as we&rsquo;re back online.</p>
        <div className="vs-maint-dots" aria-hidden="true"><i /><i /><i /></div>
        <div className="vs-maint-reconnect">Reconnecting&hellip;</div>
        <MaintStatus since={since} />
        {/* v12.10.30 (#2255): Report issue - retried until VoiceSpectrum answers (one ticket per incident, every practice) */}
        <ReportIssue kind="maintenance" downSince={since} />
      </div>
    </div>
  );
}
